import * as React from "react"
import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  RowSelectionState,
} from "@tanstack/react-table"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog"

import { DataTablePagination } from "../../../../components/ui/data-table-pagination"
import { UsersToolbar } from "./users-toolbar"
import { createUsersColumns } from "./users-columns"
import { Role, User } from "types/settings"

interface UsersDataTableProps {
  data: User[];
  roles: Role[];
  isLoading?: boolean;
  selectedUserType?: string;
  selectedRoleId?: number;
  search: string;
  onSearchChange: (value: string) => void;
  onUserTypeChange: (value?: string) => void;
  onRoleChange: (value?: number) => void;
  onDeleteUser: (user_id: string) => Promise<void>;
  onEditUser: (user: User) => void;
  onRefreshData?: () => Promise<void>;
  onCreateUser?: () => void;
}

export function UsersDataTable({ 
  data, 
  roles,
  isLoading = false,
  selectedUserType,
  selectedRoleId,
  search,
  onSearchChange,
  onUserTypeChange,
  onRoleChange,
  onDeleteUser,
  onEditUser,
  onRefreshData,
  onCreateUser
}: UsersDataTableProps) {
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({})
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState<boolean>(false)
  const [userToDelete, setUserToDelete] = React.useState<User | null>(null)
  const [isDeleting, setIsDeleting] = React.useState<boolean>(false)

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    
    setIsDeleting(true);
    try {
      await onDeleteUser(userToDelete.user_id);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      if (onRefreshData) {
        await onRefreshData();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = createUsersColumns(handleDeleteUser, onEditUser)

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  })

  return (
    <>
      <div className="h-full flex flex-col space-y-4">
        <div className="shrink-0">
          <UsersToolbar 
            table={table} 
            roles={roles}
            selectedUserType={selectedUserType}
            selectedRoleId={selectedRoleId}
            search={search}
            onSearchChange={onSearchChange}
            onUserTypeChange={onUserTypeChange}
            onRoleChange={onRoleChange}
            onCreateUser={onCreateUser}
          />
        </div>
        <div className="flex-1 min-h-0 min-w-0 rounded-lg border bg-white">
          <div className="h-full w-full overflow-auto rounded-lg">
            <table className="w-max min-w-full text-sm relative">
            <thead className="sticky top-0 z-50 bg-gray-100">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-gray-100">
                  {headerGroup.headers.map((header) => {
                    return (
                      <th 
                        key={header.id} 
                        colSpan={header.colSpan} 
                        className={`bg-gray-100 text-gray-700 font-medium px-4 py-2 text-left sticky top-0 ${header.column.id === 'actions' ? 'pr-4' : ''}`}
                        style={{ position: 'sticky', top: 0, backgroundColor: '#f3f4f6', zIndex: 50 }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
              <tbody className="bg-white">
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-gray-200">
                      {columns.map((col, idx) => (
                        <td key={idx} className="px-4 py-3">
                          <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      className="border-b border-gray-200 hover:bg-gray-50"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={`px-4 py-3 ${cell.column.id === 'actions' ? 'pr-4' : ''}`}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="h-24 text-center px-4 py-3"
                    >
                      No results.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <DataTablePagination table={table} />
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete user "{userToDelete?.name || 'Unknown User'}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
